import React, { useRef, useState } from "react";
import { Copy, Check, ChevronRight, AlertCircle, X } from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";
import { Navigation, Pagination } from "swiper/modules";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import type { ExchangeConfig } from "@/utils/exchange-config";
import { Dialog } from "./Dialog";
import { ExchangeHighlight } from "./ExchangeHighlight";

interface ExchangeTutorialContentProps {
  config: ExchangeConfig;
  exchangeCode: string;
  exchangeName: string;
  copiedIp: boolean;
  currentSlideIndex: number;
  onCopyIp: () => void;
  onSwiperInit: (swiper: SwiperType) => void;
  onSlideChange: (swiper: SwiperType) => void;
  imageContainerRef?: React.RefObject<HTMLDivElement>;
  maxImageHeight?: string;
}

export const ExchangeTutorialContent: React.FC<
  ExchangeTutorialContentProps
> = ({
  config,
  exchangeCode,
  exchangeName: _exchangeName,
  copiedIp,
  currentSlideIndex,
  onCopyIp,
  onSwiperInit,
  onSlideChange,
  imageContainerRef,
  maxImageHeight = "400px",
}) => {
  const internalImageRef = useRef<HTMLDivElement>(null);
  const activeImageRef = imageContainerRef || internalImageRef;
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);
  const [isDesktop, setIsDesktop] = useState(false);

  // Binance and Upbit keys are IP-allowlisted; other exchanges use unrestricted API keys.
  const showWhitelistIp =
    exchangeCode.startsWith("binance") || !!config.showWhitelistIp;

  // Detect if we're on desktop
  React.useEffect(() => {
    const checkDesktop = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };
    checkDesktop();
    window.addEventListener("resize", checkDesktop);
    return () => window.removeEventListener("resize", checkDesktop);
  }, []);

  // Render info content (API management, IP, security notes)
  const renderInfoContent = () => (
    <div className="space-y-4 px-10">
      <h3 className="text-lg font-semibold text-text-primary">
        🔑 {config.instructions.title}
      </h3>

      {config.highlight && <ExchangeHighlight highlight={config.highlight} />}

      {/* Exchanges without screenshots (Upbit) get the written steps */}
      {!config.tutorialSteps?.length && (
        <ol className="list-decimal pl-5 space-y-1">
          {config.instructions.steps.map((step, index) => (
            <li
              key={`step-${exchangeCode}-${index}`}
              className="text-sm text-text-secondary"
            >
              {step}
            </li>
          ))}
        </ol>
      )}

      {/* API Management Link */}
      <a
        href={config.apiManagementUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="block bg-accent/10 border border-accent/20 rounded-lg p-3 hover:bg-accent/20 transition-colors"
      >
        <div className="flex items-center justify-between">
          <span className="text-accent font-medium">
            {config.managementLinkLabel ?? "Open API Management Page"}
          </span>
          <ChevronRight className="w-5 h-5 text-accent" />
        </div>
      </a>

      {/* Whitelist IP Copy - Binance and Upbit only, all other exchanges use unrestricted API keys */}
      {showWhitelistIp && config.whitelistIp && (
        <div className="bg-primary/50 rounded-lg p-4 space-y-2">
          <p className="text-sm text-text-secondary">
            IP Address to Whitelist:
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-primary px-3 py-2 rounded text-accent font-mono">
              {config.whitelistIp}
            </code>
            <button
              onClick={onCopyIp}
              className="p-2 bg-accent hover:bg-accent-hover rounded-lg transition-colors"
            >
              {copiedIp ? (
                <Check className="w-5 h-5 text-text-primary" />
              ) : (
                <Copy className="w-5 h-5 text-text-primary" />
              )}
            </button>
          </div>
        </div>
      )}

      {/* Security Notes */}
      <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 space-y-2">
        <div className="flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-yellow-500" />
          <h4 className="font-semibold text-text-primary">Security Notes</h4>
        </div>
        <ul className="space-y-1">
          {config.instructions.securityNotes.map((note, index) => (
            <li
              key={`security-${exchangeCode}-${index}`}
              className="text-sm text-text-secondary"
            >
              • {note}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );

  return (
    <>
      <div className="space-y-6">
        {/* Carousel for tutorial images */}
        {config.tutorialSteps && config.tutorialSteps.length > 0 ? (
          <div className="space-y-4">
            <Swiper
              modules={[Navigation, Pagination]}
              navigation
              pagination={{ clickable: true }}
              spaceBetween={20}
              slidesPerView={1}
              autoHeight={true}
              initialSlide={currentSlideIndex}
              onSwiper={onSwiperInit}
              onSlideChange={onSlideChange}
              className="tutorial-swiper rounded-lg"
              style={
                {
                  "--swiper-pagination-color": "#3b82f6",
                  "--swiper-navigation-color": "#ffffff",
                  paddingBottom: "40px",
                } as React.CSSProperties
              }
            >
              {/* Desktop: First slide with info content */}
              {isDesktop && (
                <SwiperSlide key={`info-${exchangeCode}`}>
                  {renderInfoContent()}
                </SwiperSlide>
              )}

              {/* Tutorial image slides */}
              {config.tutorialSteps.map((step, index) => (
                <SwiperSlide key={`tutorial-${exchangeCode}-${index}`}>
                  <div className="space-y-4 xl:px-10">
                    <div
                      ref={index === currentSlideIndex ? activeImageRef : null}
                      className="relative bg-primary rounded-lg overflow-hidden"
                    >
                      {isDesktop ? (
                        // Desktop: Simple image with click to open fullscreen
                        <button
                          type="button"
                          onClick={() => setFullscreenImage(step.image)}
                          className="w-full cursor-pointer focus:outline-none focus:ring-2 focus:ring-accent rounded-lg"
                        >
                          <img
                            src={step.image}
                            alt={step.description}
                            className="w-full h-auto object-contain"
                            style={{ maxHeight: maxImageHeight }}
                          />
                        </button>
                      ) : (
                        // Mobile: Keep zoom/pan functionality
                        <TransformWrapper
                          initialScale={1}
                          minScale={1}
                          maxScale={4}
                          doubleClick={{ mode: "reset" }}
                          wheel={{ step: 0.1 }}
                          pinch={{ step: 5 }}
                          centerOnInit
                        >
                          {({ resetTransform }) => (
                            <TransformComponent
                              wrapperClass="w-full"
                              contentClass="w-full flex items-center justify-center"
                            >
                              <img
                                src={step.image}
                                alt={step.description}
                                className="w-full h-auto object-contain select-none"
                                style={{ maxHeight: maxImageHeight }}
                                onDoubleClick={() => resetTransform()}
                                onLoad={() => {
                                  if (
                                    index === currentSlideIndex &&
                                    activeImageRef.current
                                  ) {
                                    const event = new Event("resize");
                                    globalThis.dispatchEvent(event);
                                  }
                                }}
                              />
                            </TransformComponent>
                          )}
                        </TransformWrapper>
                      )}
                    </div>
                    {step.description && (
                      <p className="text-center text-text-primary font-medium px-2 pb-2">
                        Step {index + 1}: {step.description}
                      </p>
                    )}
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        ) : (
          // No tutorial steps - show info content directly
          renderInfoContent()
        )}

        {/* Mobile: Show info content below slider */}
        {!isDesktop &&
          config.tutorialSteps &&
          config.tutorialSteps.length > 0 && (
            <div className="space-y-6">
              {/* API Management Link */}
              <a
                href={config.apiManagementUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block bg-accent/10 border border-accent/20 rounded-lg p-3 hover:bg-accent/20 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <span className="text-accent font-medium">
                    {config.managementLinkLabel ?? "Open API Management Page"}
                  </span>
                  <ChevronRight className="w-5 h-5 text-accent" />
                </div>
              </a>

              {/* Whitelist IP Copy - Binance and Upbit only, all other exchanges use unrestricted API keys */}
              {showWhitelistIp && config.whitelistIp && (
                <div className="bg-primary/50 rounded-lg p-4 space-y-2">
                  <p className="text-sm text-text-secondary">
                    IP Address to Whitelist:
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-primary px-3 py-2 rounded text-accent font-mono">
                      {config.whitelistIp}
                    </code>
                    <button
                      onClick={onCopyIp}
                      className="p-2 bg-accent hover:bg-accent-hover rounded-lg transition-colors"
                    >
                      {copiedIp ? (
                        <Check className="w-5 h-5 text-text-primary" />
                      ) : (
                        <Copy className="w-5 h-5 text-text-primary" />
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Security Notes */}
              <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-yellow-500" />
                  <h4 className="font-semibold text-text-primary">
                    Security Notes
                  </h4>
                </div>
                <ul className="space-y-1">
                  {config.instructions.securityNotes.map((note, index) => (
                    <li
                      key={`security-${exchangeCode}-${index}`}
                      className="text-sm text-text-secondary"
                    >
                      • {note}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
      </div>

      {/* Fullscreen Image Dialog */}
      <Dialog
        open={!!fullscreenImage}
        onOpenChange={() => setFullscreenImage(null)}
        showCloseButton={false}
        maxWidth="4xl"
        className="!max-w-[95vw] !max-h-[95vh] !bg-black/95"
        contentClassName="!p-0 flex items-center justify-center"
        overlayClassName="!bg-black/90"
      >
        <div className="relative w-full h-full flex items-center justify-center p-4">
          <button
            onClick={() => setFullscreenImage(null)}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors z-10"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          {fullscreenImage && (
            <img
              src={fullscreenImage}
              alt="Fullscreen view"
              className="max-w-full max-h-[90vh] object-contain"
            />
          )}
        </div>
      </Dialog>
    </>
  );
};
